import type { ScraperCrawlErrorCategory, ScraperCrawlRoute } from '@/gql/graphql';

/**
 * Where the Scraper section's screens live.
 *
 * Front-end knowledge, kept here rather than served by the API — the same split
 * `WORKFLOW_ROUTES` makes.
 */
export const SCRAPER_ROUTES = {
  overview: '/dashboard/scraper',
  targets: '/dashboard/scraper/targets',
  tools: '/dashboard/scraper/tools',
  crawls: '/dashboard/scraper/crawls',
  queues: '/dashboard/scraper/queues',
  unrecognised: '/dashboard/scraper/unrecognised',
} as const;

export const SCRAPER_TABS: { label: string; href: string }[] = [
  { label: 'Overview', href: SCRAPER_ROUTES.overview },
  { label: 'Targets', href: SCRAPER_ROUTES.targets },
  { label: 'Tools', href: SCRAPER_ROUTES.tools },
  { label: 'Unrecognised', href: SCRAPER_ROUTES.unrecognised },
  { label: 'Crawls', href: SCRAPER_ROUTES.crawls },
  { label: 'Queues', href: SCRAPER_ROUTES.queues },
];

/** A crawl target is a brand, so this takes the brand's id. */
export function scraperCrawlTargetHref(brandId: string): string {
  return `${SCRAPER_ROUTES.targets}/${brandId}`;
}

export function scraperCrawlHref(id: string): string {
  return `${SCRAPER_ROUTES.crawls}/${id}`;
}

export function scraperToolHref(toolId: string): string {
  return `${SCRAPER_ROUTES.tools}/${toolId}`;
}

/** A domain as stored on a brand, made safe to put in an `href`. */
export function externalDomainHref(domain: string | null | undefined): string | null {
  if (!domain) return null;
  const trimmed = domain.trim();
  if (!trimmed) return null;
  const url = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(url);
    // Anything but http(s) — `javascript:`, `data:` — is refused outright.
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}

/**
 * Why a crawl failed, in words.
 *
 * An exhaustive `Record` over the generated enum on purpose: adding a category
 * to the schema then fails to build here until somebody writes the copy, rather
 * than rendering a raw SCREAMING_SNAKE string in the UI.
 */
export const CRAWL_ERROR_LABELS: Record<ScraperCrawlErrorCategory, string> = {
  DNS: 'DNS',
  PROXY: 'Proxy',
  BROWSER_SESSION: 'Browser session',
  TIMEOUT: 'Timed out',
  CONNECTION: 'Connection',
  TLS: 'Certificate',
  AUTH_CONFIG: 'Auth config',
  RUNTIME: 'Runtime',
  UNKNOWN: 'Unknown',
};

/** Which browser a crawl went through, cheapest first. Only Bright Data is billed. */
export const CRAWL_ROUTES: Record<
  ScraperCrawlRoute,
  { label: string; tone: 'neutral' | 'info' | 'warning' }
> = {
  DIRECT: { label: 'AWS', tone: 'neutral' },
  RELAY: { label: 'deepthink', tone: 'info' },
  BRIGHT_DATA: { label: 'Bright Data', tone: 'warning' },
};

export const CRAWL_ROUTE_ORDER: ScraperCrawlRoute[] = ['DIRECT', 'RELAY', 'BRIGHT_DATA'];
